import { motion } from "framer-motion";

export function PageHeader({ overline, title, description, right, testid }) {
  return (
    <div data-testid={testid} className="border-b border-zinc-200 bg-white px-10 pt-10 pb-8">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="flex items-end justify-between gap-6"
      >
        <div>
          {overline && <div className="overline text-zinc-500 mb-3">{overline}</div>}
          <h1 className="font-heading text-4xl tracking-tight text-zinc-900">{title}</h1>
          {description && (
            <p className="mt-2 max-w-2xl text-sm text-zinc-600 leading-relaxed">{description}</p>
          )}
        </div>
        {right && <div className="flex items-center gap-3">{right}</div>}
      </motion.div>
    </div>
  );
}

export function Section({ children, className = "" }) {
  return <section className={`px-10 py-8 ${className}`}>{children}</section>;
}

export function Card({ children, className = "", ai = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`bg-white border border-zinc-200 ${ai ? "ai-tracing-beam" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

export function CardHeader({ overline, title, right, className = "" }) {
  return (
    <div className={`flex items-start justify-between gap-4 px-6 pt-5 pb-3 border-b border-zinc-100 ${className}`}>
      <div>
        {overline && <div className="overline text-zinc-500 mb-1">{overline}</div>}
        {title && <div className="font-heading text-base text-zinc-900">{title}</div>}
      </div>
      {right}
    </div>
  );
}

export function Pill({ children, tone = "neutral", className = "", testid }) {
  const tones = {
    neutral: "bg-zinc-100 text-zinc-800 border-zinc-200",
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warn: "bg-amber-50 text-amber-800 border-amber-200",
    danger: "bg-rose-50 text-rose-800 border-rose-200",
    ai: "bg-blue-50 text-blue-800 border-blue-200",
    info: "bg-zinc-900 text-white border-zinc-900",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium border rounded-sm ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

export function Stat({ label, value, suffix = "", delta = null, testid, large = false }) {
  return (
    <div data-testid={testid}>
      <div className="overline text-zinc-500 mb-2">{label}</div>
      <div className={`metric-value ${large ? "text-5xl sm:text-6xl" : "text-4xl"} text-zinc-900 flex items-baseline gap-2`}>
        {value}
        {suffix && <span className="text-zinc-400 text-xl font-mono">{suffix}</span>}
      </div>
      {delta !== null && (
        <div className={`mt-2 text-xs font-mono ${delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
          {delta >= 0 ? "▲" : "▼"} {Math.abs(delta)} vs prior
        </div>
      )}
    </div>
  );
}

export function LoadingBar({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-3 px-10 py-8 text-zinc-500 text-sm">
      <div className="h-px w-10 bg-zinc-300 relative overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 w-1/3 bg-zinc-900"
          animate={{ x: ["-100%", "300%"] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
      <span className="font-mono text-xs uppercase tracking-widest">{label}</span>
    </div>
  );
}
